import React, { useEffect, useState } from "react";
import { Breadcrumbs } from "../AdminLayout";
import { derivativeRequestsApi } from "../content/api";
import { formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { Layers, Plus, Trash2 } from "lucide-react";

const TABS = ["queued", "in_progress", "delivered", "rejected"];

export default function DerivativeQueue() {
  const [tab, setTab] = useState("queued");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ infographic_id: "", purpose: "web", target_width_px: 1200, target_height_px: 800, target_format: "webp", notes: "" });

  const reload = () => {
    derivativeRequestsApi.list({ status: tab }).then(setRows).catch(e => toast.error(formatErr(e.response?.data?.detail)));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tab]);

  const add = async () => {
    if (!form.infographic_id) { toast.error("Infographic id required"); return; }
    try {
      await derivativeRequestsApi.create({ ...form,
        target_width_px: Number(form.target_width_px) || 0,
        target_height_px: Number(form.target_height_px) || 0,
      });
      setForm({ infographic_id: "", purpose: "web", target_width_px: 1200, target_height_px: 800, target_format: "webp", notes: "" });
      toast.success("Request queued"); reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const move = async (id, status) => {
    try { await derivativeRequestsApi.update(id, { status }); toast.success(`Marked ${status}`); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const deliver = async (id) => {
    const url = window.prompt("Delivered URL:", "");
    if (!url) return;
    try { await derivativeRequestsApi.update(id, { status: "delivered", delivered_url: url }); toast.success("Delivered"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await derivativeRequestsApi.remove(id); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const inputCls = "border border-line px-3 py-2 text-sm w-full";
  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="derivative-queue">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={[{ label: "Media Library" }, { label: "Derivative Requests" }]}/>
      <div className="font-serif text-3xl text-navy mt-4 mb-6 flex items-center gap-3"><Layers size={22}/> Infographic Derivative Request Queue</div>

      <div className="bg-white border border-line p-5 mb-5">
        <div className="mono text-[10px] uppercase tracking-widest text-prestige mb-3">Queue a new request</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <input data-testid="dq-infoid" placeholder="Infographic id" value={form.infographic_id} onChange={e => setForm(f => ({ ...f, infographic_id: e.target.value }))} className={inputCls}/>
          <select data-testid="dq-purpose" value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} className={inputCls}>
            <option>web</option><option>thumbnail</option><option>social</option><option>print</option><option>mobile</option>
          </select>
          <input data-testid="dq-w" type="number" placeholder="Width px" value={form.target_width_px} onChange={e => setForm(f => ({ ...f, target_width_px: e.target.value }))} className={inputCls}/>
          <input data-testid="dq-h" type="number" placeholder="Height px" value={form.target_height_px} onChange={e => setForm(f => ({ ...f, target_height_px: e.target.value }))} className={inputCls}/>
          <input data-testid="dq-format" placeholder="Format (webp/png)" value={form.target_format} onChange={e => setForm(f => ({ ...f, target_format: e.target.value }))} className={inputCls}/>
          <button data-testid="dq-add" onClick={add} className="bg-navy hover:bg-navy-hover text-white text-sm px-4 py-2 inline-flex items-center gap-2 justify-center"><Plus size={13}/> Queue</button>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-line mb-4">
        {TABS.map(t => (
          <button key={t} data-testid={`dq-tab-${t}`} onClick={() => setTab(t)}
                  className={`mono text-[10px] uppercase tracking-widest px-4 py-2 border-b-2 -mb-px ${tab === t ? "border-navy text-navy" : "border-transparent text-stone2 hover:text-navy"}`}>
            {t.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="bg-white border border-line p-8 text-center text-sm text-stone2">No requests in {tab.replace(/_/g, " ")}.</div>
      ) : (
        <ul className="space-y-2">
          {rows.map(r => (
            <li key={r.id} data-testid={`dq-row-${r.id}`} className="bg-white border border-line px-4 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-navy truncate">{r.purpose} · {r.target_width_px}×{r.target_height_px} {r.target_format}</div>
                <div className="mono text-[10px] text-stone2 truncate">infographic {r.infographic_id} · requested {r.created_at}</div>
                {r.notes && <div className="text-xs text-stone2 italic mt-1">"{r.notes}"</div>}
                {r.delivered_url && <div className="text-xs text-navy mt-1 truncate">Delivered → <a href={r.delivered_url} target="_blank" rel="noreferrer" className="underline">{r.delivered_url}</a></div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.status === "queued" && (<button data-testid={`dq-start-${r.id}`} onClick={() => move(r.id, "in_progress")} className="mono text-[10px] uppercase tracking-widest border border-line hover:border-navy text-navy px-2 py-1">Start</button>)}
                {r.status === "in_progress" && (<button data-testid={`dq-deliver-${r.id}`} onClick={() => deliver(r.id)} className="mono text-[10px] uppercase tracking-widest border border-[#3F6250] text-[#3F6250] hover:bg-[#3F6250] hover:text-white px-2 py-1">Deliver</button>)}
                {r.status !== "rejected" && (<button data-testid={`dq-reject-${r.id}`} onClick={() => move(r.id, "rejected")} className="mono text-[10px] uppercase tracking-widest text-[#9F1239] hover:underline px-2 py-1">Reject</button>)}
                <button data-testid={`dq-remove-${r.id}`} onClick={() => remove(r.id)} className="text-[#9F1239]"><Trash2 size={13}/></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
